import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { dayjs } from "@/utils/date";
import {
  useCreateSpecialEvent,
  useDeleteSpecialEvent,
  useSpecialEvents,
  useUpdateSpecialEvent,
} from "@/hooks/use-special-events";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { ApiSpecialEvent, ApiSpecialEventCreate } from "@/types/api";

function formatDate(value: string): string {
  return dayjs(value)
    .format("ddd D [de] MMM YYYY")
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatTime12h(value: string | null): string {
  if (!value) return "";
  const [h, m] = value.split(":");
  let hour = Number(h);
  const period = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${m} ${period}`;
}

/** Human summary of which overrides an event applies, as chips. */
function eventBadges(e: ApiSpecialEvent): string[] {
  const chips: string[] = [];
  if (e.open_time && e.close_time) {
    chips.push(`Horario ${formatTime12h(e.open_time)}–${formatTime12h(e.close_time)}`);
  }
  if (e.reservation_duration_minutes != null) {
    const h = Math.floor(e.reservation_duration_minutes / 60);
    const m = e.reservation_duration_minutes % 60;
    chips.push(`Duración ${h}h${m ? ` ${m}m` : ""}`);
  }
  if (e.bypass_kitchen_cap) chips.push("Sin límite de cocina");
  else if (e.kitchen_cap_override != null) chips.push(`Cocina ${e.kitchen_cap_override} pax`);
  return chips;
}

const EMPTY_DRAFT = {
  name: "",
  date: "",
  end_date: "",
  open_time: "",
  close_time: "",
  reservation_duration_minutes: "",
  bypass_kitchen_cap: false,
  kitchen_cap_override: "",
  notes: "",
};

// ``<input type="time">`` wants "HH:MM"; the API stores "HH:MM:SS".
function draftFromEvent(e: ApiSpecialEvent): typeof EMPTY_DRAFT {
  return {
    name: e.name,
    date: e.date,
    end_date: e.end_date ?? "",
    open_time: e.open_time ? e.open_time.slice(0, 5) : "",
    close_time: e.close_time ? e.close_time.slice(0, 5) : "",
    reservation_duration_minutes:
      e.reservation_duration_minutes != null ? String(e.reservation_duration_minutes) : "",
    bypass_kitchen_cap: e.bypass_kitchen_cap,
    kitchen_cap_override: e.kitchen_cap_override != null ? String(e.kitchen_cap_override) : "",
    notes: e.notes ?? "",
  };
}

export function SpecialEventsPanel() {
  const { data: events, isPending } = useSpecialEvents();
  const createMut = useCreateSpecialEvent();
  const updateMut = useUpdateSpecialEvent();
  const deleteMut = useDeleteSpecialEvent();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [confirmDelete, setConfirmDelete] = useState<ApiSpecialEvent | null>(null);

  const set = (patch: Partial<typeof draft>) => setDraft((d) => ({ ...d, ...patch }));
  const saving = createMut.isPending || updateMut.isPending;

  const cancel = () => {
    setAdding(false);
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT });
  };

  const startEdit = (e: ApiSpecialEvent) => {
    setDraft(draftFromEvent(e));
    setEditingId(e.id);
    setAdding(true);
  };

  const canSubmit =
    draft.name.trim().length > 0 &&
    !!draft.date &&
    (!draft.end_date || draft.end_date >= draft.date) &&
    // Hours: both or neither.
    !!draft.open_time === !!draft.close_time &&
    (!draft.open_time || !draft.close_time || draft.open_time < draft.close_time) &&
    // Kitchen cap: bypass and override are mutually exclusive.
    !(draft.bypass_kitchen_cap && draft.kitchen_cap_override);

  const submit = () => {
    if (!canSubmit) return;
    const payload: ApiSpecialEventCreate = {
      name: draft.name.trim(),
      date: draft.date,
      end_date: draft.end_date || null,
      open_time: draft.open_time || null,
      close_time: draft.close_time || null,
      reservation_duration_minutes: draft.reservation_duration_minutes
        ? Number(draft.reservation_duration_minutes)
        : null,
      bypass_kitchen_cap: draft.bypass_kitchen_cap,
      kitchen_cap_override:
        !draft.bypass_kitchen_cap && draft.kitchen_cap_override
          ? Number(draft.kitchen_cap_override)
          : null,
      notes: draft.notes.trim(),
    };
    if (editingId) {
      updateMut.mutate({ id: editingId, changes: payload }, { onSuccess: cancel });
    } else {
      createMut.mutate(payload, { onSuccess: cancel });
    }
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="size-6 text-text-secondary" />
      </div>
    );
  }

  const sorted = [...(events ?? [])].toSorted((a, b) => a.date.localeCompare(b.date));

  return (
    <section className="flex flex-col gap-4 max-w-2xl">
      <header>
        <h2 className="text-xl font-semibold tracking-tight">Eventos especiales</h2>
        <p className="text-sm text-text-secondary mt-1">
          Para días con configuración distinta — un partido de Colombia, una fecha especial. Puedes
          abrir en otro horario, mantener las mesas ocupadas más tiempo (para que nadie reserve
          encima de gente que sigue viendo el partido) y/o levantar el límite de cocina.
        </p>
      </header>

      <div className="flex flex-col gap-2">
        {sorted.map((e) => {
          const end = e.end_date && e.end_date !== e.date ? ` → ${formatDate(e.end_date)}` : "";
          return (
            <div
              key={e.id}
              className="flex items-start gap-3 rounded-xl border border-border bg-white p-3"
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{e.name}</div>
                <div className="text-[13px] text-text-muted">
                  {formatDate(e.date)}
                  {end}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {eventBadges(e).map((chip) => (
                    <span
                      key={chip}
                      className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-text-secondary"
                    >
                      {chip}
                    </span>
                  ))}
                  {eventBadges(e).length === 0 && (
                    <span className="text-[11px] text-text-muted">Sin overrides</span>
                  )}
                </div>
                {e.notes && <div className="text-[12px] text-text-muted mt-1">{e.notes}</div>}
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Editar evento"
                onClick={() => startEdit(e)}
                className="shrink-0 text-text-secondary hover:bg-muted"
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Eliminar evento"
                onClick={() => setConfirmDelete(e)}
                className="shrink-0 text-red-500 hover:bg-red-50"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          );
        })}
        {sorted.length === 0 && !adding && (
          <p className="text-sm text-text-muted py-2">No hay eventos especiales configurados.</p>
        )}
      </div>

      {adding ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 flex flex-col gap-3">
          <div className="text-sm font-semibold">
            {editingId ? "Editar evento" : "Nuevo evento"}
          </div>
          <div className="flex flex-col gap-1">
            <Label>Nombre del evento</Label>
            <Input
              value={draft.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Ej. Partido Colombia vs Argentina"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={draft.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Hasta (opcional)</Label>
              <Input
                type="date"
                value={draft.end_date}
                min={draft.date || undefined}
                onChange={(e) => set({ end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Abre (opcional)</Label>
              <Input
                type="time"
                value={draft.open_time}
                onChange={(e) => set({ open_time: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Cierra (opcional)</Label>
              <Input
                type="time"
                value={draft.close_time}
                onChange={(e) => set({ close_time: e.target.value })}
              />
            </div>
          </div>
          <p className="text-[11px] text-text-muted -mt-2">
            Deja el horario vacío para usar el horario normal del día.
          </p>

          <div className="flex flex-col gap-1">
            <Label>Duración de las reservas (minutos, opcional)</Label>
            <Input
              type="number"
              min={15}
              step={15}
              value={draft.reservation_duration_minutes}
              onChange={(e) => set({ reservation_duration_minutes: e.target.value })}
              placeholder="Ej. 300 para que se queden todo el partido"
            />
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-border bg-white p-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Sin límite de cocina</Label>
                <p className="text-[11px] text-text-muted">Levanta el techo de pax por franja.</p>
              </div>
              <Switch
                checked={draft.bypass_kitchen_cap}
                onCheckedChange={(v) => set({ bypass_kitchen_cap: v, kitchen_cap_override: "" })}
              />
            </div>
            {!draft.bypass_kitchen_cap && (
              <div className="flex flex-col gap-1">
                <Label className="text-[12px]">O un límite específico (pax, opcional)</Label>
                <Input
                  type="number"
                  min={1}
                  value={draft.kitchen_cap_override}
                  onChange={(e) => set({ kitchen_cap_override: e.target.value })}
                  placeholder="Ej. 40"
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label>Notas (opcional)</Label>
            <Input value={draft.notes} onChange={(e) => set({ notes: e.target.value })} />
          </div>

          <div className="flex gap-2 mt-1">
            <Button onClick={submit} disabled={!canSubmit || saving} className="flex-1">
              {saving ? (
                <>
                  <Spinner className="mr-2 size-4" /> Guardando…
                </>
              ) : editingId ? (
                "Guardar cambios"
              ) : (
                "Guardar evento"
              )}
            </Button>
            <Button variant="ghost" onClick={cancel}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setDraft({ ...EMPTY_DRAFT });
            setEditingId(null);
            setAdding(true);
          }}
          className="self-start"
        >
          <Plus className="size-4" /> Agregar evento
        </Button>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este evento?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  Se eliminará{" "}
                  <span className="font-medium text-foreground">{confirmDelete.name}</span> (
                  {formatDate(confirmDelete.date)}). El día vuelve a su configuración normal.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) {
                  deleteMut.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default SpecialEventsPanel;
